import { createContext, Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from "react";
import { FCX } from "@datassential/platform-ui-lib";
import { ArtificialMetric, VisualizeMetric } from "@api/useVisualizeData";
import { Metric } from "@models/Metric";
import { containsAll } from "@core/utils/array";

export enum VisualizeView {
    FamiliarFavorites,
    RisingStars,
    Polarity,
}

export const VISUALIZE_VIEWS_ORDER = [
    VisualizeView.FamiliarFavorites,
    VisualizeView.RisingStars,
    VisualizeView.Polarity,
];

export const VISUALIZE_VIEWS: Map<VisualizeView, {
    id: VisualizeView;
    name: string;
    xMetricId: VisualizeMetric;
    yMetricId: VisualizeMetric;
}> = new Map([
    [VisualizeView.FamiliarFavorites, {
        id: VisualizeView.FamiliarFavorites,
        name: "Familiar Favorites",
        xMetricId: Metric.KnowIt,
        yMetricId: Metric.LoveLikeIt,
    }],
    [VisualizeView.RisingStars, {
        id: VisualizeView.RisingStars,
        name: "Rising Stars",
        xMetricId: ArtificialMetric.KnowItChange,
        yMetricId: Metric.LoveIt,
    }],
    [VisualizeView.Polarity, {
        id: VisualizeView.Polarity,
        name: "Polarity",
        xMetricId: Metric.HateIt,
        yMetricId: Metric.LoveIt,
    }],
]);

type Data = {
    xMetricId: VisualizeMetric;
    setXMetricId: Dispatch<SetStateAction<VisualizeMetric>>;
    yMetricId: VisualizeMetric;
    setYMetricId: Dispatch<SetStateAction<VisualizeMetric>>;
    activeView: VisualizeView | null;
    setActiveView: (view: VisualizeView) => void;
};

const DEFAULTS: Data = {
    xMetricId: Metric.KnowIt,
    setXMetricId: () => null,
    yMetricId: Metric.LoveLikeIt,
    setYMetricId: () => null,
    activeView: null,
    setActiveView: () => null,
}

const VisualizeContext = createContext<Data>(DEFAULTS);

export function useVisualizeContext() {
    return useContext(VisualizeContext);
}

export const VisualizeContextProvider: FCX = ({
    children,
}) => {
    const [xMetricId, setXMetricId] = useState<Data["xMetricId"]>(DEFAULTS["xMetricId"]);
    const [yMetricId, setYMetricId] = useState<Data["yMetricId"]>(DEFAULTS["yMetricId"]);

    const getIsViewActive = useCallback(
        (view: VisualizeView) => {
            const {
                xMetricId: viewXMetricId,
                yMetricId: viewYMetricId,
            } = VISUALIZE_VIEWS.get(view) ?? {};

            return containsAll([xMetricId, yMetricId], [viewXMetricId, viewYMetricId]);
        },
        [xMetricId, yMetricId]
    );

    const setActiveView = useCallback(
        (view: VisualizeView) => {
            const { xMetricId, yMetricId } = VISUALIZE_VIEWS.get(view) ?? {};
            if (xMetricId && yMetricId) {
                setXMetricId(xMetricId);
                setYMetricId(yMetricId);
            }
        },
        []
    );

    const isFamiliarFavoritesActive = useMemo(
        () => getIsViewActive(VisualizeView.FamiliarFavorites),
        [getIsViewActive]
    );

    const isRisingStarsActive = useMemo(
        () => getIsViewActive(VisualizeView.RisingStars),
        [getIsViewActive]
    );

    const isPolarityActive = useMemo(
        () => getIsViewActive(VisualizeView.Polarity),
        [getIsViewActive]
    );

    const activeView = useMemo(
        () => {
            if (isFamiliarFavoritesActive) return VisualizeView.FamiliarFavorites;
            if (isRisingStarsActive) return VisualizeView.RisingStars;
            if (isPolarityActive) return VisualizeView.Polarity;
            return null;
        },
        [isFamiliarFavoritesActive, isRisingStarsActive, isPolarityActive]
    );

    const contextValue = useMemo(
        (): Data => ({
            xMetricId,
            setXMetricId,
            yMetricId,
            setYMetricId,
            activeView,
            setActiveView,
        }),
        [
            xMetricId,
            yMetricId,
            activeView,
            setActiveView,
        ]
    );

    return (
        <VisualizeContext.Provider value={contextValue}>
            {children}
        </VisualizeContext.Provider>
    );
};